import React, { useEffect, useState } from "react";
import Select from "react-select";
import moment from "moment";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
    autopostingdues,
    autopostingsubmit,
    grouplist,
    groupmemberlist,
    individualmemberlist,
} from "../../../services/admin";

/**
 * Auto Posting - bulk back-dated posting of old dues.
 *
 * Used when an existing book is migrated into the system: the scheme, staff,
 * members, loan allocation and approval are keyed in by hand, but the dues the
 * members already paid before go-live are posted here in one go. Pick a group
 * (or a single member), pick the date the old records run up to, confirm the
 * dues that come back, and Auto Post saves them with the paid date set to each
 * due's own date.
 */
export default function AutoPosting() {
    const [postingType, setPostingType] = useState("group");
    const [glist, setGlist] = useState([]);
    const [group, setGroup] = useState("");
    const [mlist, setMlist] = useState([]);
    const [memid, setMemId] = useState("");
    const [uptoDate, setUptoDate] = useState("");
    const [remarks, setRemarks] = useState("");
    const [createVoucher, setCreateVoucher] = useState(true);

    const [members, setMembers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [selected, setSelected] = useState({});
    const [loading, setLoading] = useState(false);
    const [posting, setPosting] = useState(false);

    useEffect(() => {
        const temp = new FormData();
        temp.append("branch", localStorage.getItem("branch"));
        grouplist(temp).then((res) => {
            if (res?.status === true) {
                setGlist(
                    res.data.map((item) => ({ value: item.id, label: item.group_name }))
                );
            }
        });
    }, []);

    // Individual mode lists every member holding an individual loan; group mode
    // lists the members of the chosen group.
    useEffect(() => {
        if (postingType !== "individual") return;
        const temp = new FormData();
        temp.append("branch", localStorage.getItem("branch"));
        individualmemberlist(temp).then((res) => {
            if (res?.status === true) {
                setMlist(res.data);
            }
        });
    }, [postingType]);

    function clearResult() {
        setMembers([]);
        setSummary(null);
        setSelected({});
    }

    function handleType(type) {
        setPostingType(type);
        setGroup("");
        setMemId("");
        setMlist([]);
        clearResult();
    }

    function handlegroup(e) {
        setGroup(e || "");
        setMemId("");
        setMlist([]);
        clearResult();
        if (e) {
            groupmemberlist(e.value).then((res) => {
                if (res?.status === true) {
                    setMlist(res.data);
                }
            });
        }
    }

    function handleShow() {
        if (postingType === "group" && !group) {
            toast.error("Please select a group");
            return;
        }
        if (postingType === "individual" && !memid) {
            toast.error("Please select a member");
            return;
        }
        if (!uptoDate) {
            toast.error("Please select a date");
            return;
        }

        setLoading(true);
        clearResult();

        autopostingdues({
            posting_type: postingType,
            group_id: postingType === "group" ? group.value : "",
            member_id: memid,
            upto_date: uptoDate,
        })
            .then((res) => {
                if (res?.status === true) {
                    setMembers(res.data.members || []);
                    setSummary(res.data.summary || null);
                    // Everything the operator asked for starts ticked; they
                    // untick the odd due that was never actually collected.
                    const preset = {};
                    (res.data.members || []).forEach((m) =>
                        m.dues.forEach((d) => {
                            preset[d.entry_id] = true;
                        })
                    );
                    setSelected(preset);
                    if (!(res.data.members || []).length) {
                        toast.info("No pending dues found on or before this date");
                    }
                } else if (res) {
                    toast.error(res.message);
                }
            })
            .finally(() => setLoading(false));
    }

    function toggleDue(entryId) {
        setSelected((prev) => ({ ...prev, [entryId]: !prev[entryId] }));
    }

    function toggleMember(member) {
        const allOn = member.dues.every((d) => selected[d.entry_id]);
        setSelected((prev) => {
            const next = { ...prev };
            member.dues.forEach((d) => {
                next[d.entry_id] = !allOn;
            });
            return next;
        });
    }

    function toggleAll() {
        const allOn = selectedIds.length === totalDueCount;
        const next = {};
        members.forEach((m) =>
            m.dues.forEach((d) => {
                next[d.entry_id] = !allOn;
            })
        );
        setSelected(next);
    }

    const selectedIds = Object.keys(selected).filter((id) => selected[id]);
    const totalDueCount = members.reduce((sum, m) => sum + m.dues.length, 0);
    const selectedAmount = members.reduce(
        (sum, m) =>
            sum +
            m.dues.reduce(
                (s, d) => s + (selected[d.entry_id] ? Number(d.total) : 0),
                0
            ),
        0
    );

    function handlePost() {
        if (!selectedIds.length) {
            toast.error("Please select at least one due");
            return;
        }

        Swal.fire({
            title: "Confirm Auto Posting",
            html:
                `<b>${selectedIds.length}</b> due(s) totalling <b>₹${selectedAmount.toLocaleString(
                    "en-IN"
                )}</b> will be posted as collected.<br/>` +
                "Each due is saved with its paid date set to its own due date.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#6259ca",
            cancelButtonColor: "#ac0404",
            confirmButtonText: "Auto Post",
            allowOutsideClick: false,
        }).then((result) => {
            if (!result.isConfirmed) return;

            setPosting(true);
            autopostingsubmit({
                posting_type: postingType,
                entry_ids: selectedIds,
                remarks: remarks,
                create_voucher: createVoucher,
            })
                .then((res) => {
                    if (res?.status === true) {
                        toast.success(
                            `${res.data.posted_dues} due(s) posted in ${res.data.collections} collection(s)`
                        );
                        handleShow();
                    } else if (res) {
                        toast.error(res.message);
                    }
                })
                .finally(() => setPosting(false));
        });
    }

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">Auto Posting</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Collection</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        Auto Posting
                    </li>
                </ol>
            </div>

            <div className="content-body">
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-header">
                                <h2 className="main-content-title tx-24 mg-b-5">
                                    Group / Individual Posting
                                </h2>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-xl-3 col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">
                                                Posting Type<span className="text-danger"> *</span>
                                            </label>
                                            <div>
                                                <label className="me-4">
                                                    <input
                                                        type="radio"
                                                        className="me-1"
                                                        checked={postingType === "group"}
                                                        onChange={() => handleType("group")}
                                                    />
                                                    Group
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        className="me-1"
                                                        checked={postingType === "individual"}
                                                        onChange={() => handleType("individual")}
                                                    />
                                                    Individual
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {postingType === "group" && (
                                        <div className="col-xl-3 col-sm-6">
                                            <div className="mb-4">
                                                <label className="form-label">
                                                    Group<span className="text-danger"> *</span>
                                                </label>
                                                <Select
                                                    className="default-select"
                                                    placeholder="Select Group"
                                                    value={group}
                                                    options={glist}
                                                    onChange={handlegroup}
                                                    isSearchable
                                                    isClearable
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="col-xl-3 col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">
                                                Member
                                                {postingType === "individual" && (
                                                    <span className="text-danger"> *</span>
                                                )}
                                            </label>
                                            <select
                                                className="form-control"
                                                value={memid}
                                                onChange={(e) => {
                                                    setMemId(e.target.value);
                                                    clearResult();
                                                }}
                                            >
                                                <option value="">
                                                    {postingType === "individual"
                                                        ? "Select Member"
                                                        : "All Members"}
                                                </option>
                                                {mlist.map((item) => (
                                                    <option key={item.id} value={item.id}>
                                                        {item.member_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-xl-3 col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">
                                                Posting Up To Date
                                                <span className="text-danger"> *</span>
                                            </label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={uptoDate}
                                                onChange={(e) => {
                                                    setUptoDate(e.target.value);
                                                    clearResult();
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-xl-3 col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">Remarks</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Old record posting"
                                                value={remarks}
                                                onChange={(e) => setRemarks(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-xl-3 col-sm-6">
                                        <div className="mb-4">
                                            <label className="form-label">Accounting</label>
                                            <div>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        className="me-1"
                                                        checked={createVoucher}
                                                        onChange={(e) =>
                                                            setCreateVoucher(e.target.checked)
                                                        }
                                                    />
                                                    Create receipt vouchers
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-xl-3 col-sm-6 d-flex align-items-center">
                                        <button
                                            className="btn btn-primary mb-4"
                                            onClick={handleShow}
                                            disabled={loading}
                                        >
                                            {loading ? "Loading..." : "Show Dues"}
                                        </button>
                                    </div>
                                </div>

                                {summary && (
                                    <div className="alert alert-info mt-2">
                                        {summary.member_count} member(s), {summary.due_count} pending
                                        due(s) up to{" "}
                                        {moment(uptoDate).format("DD-MM-YYYY")} — total ₹
                                        {Number(summary.due_amount).toLocaleString("en-IN")}
                                    </div>
                                )}

                                {members.length > 0 && (
                                    <>
                                        <div className="table-container mt-3">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: "40px" }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={
                                                                    selectedIds.length === totalDueCount
                                                                }
                                                                onChange={toggleAll}
                                                            />
                                                        </th>
                                                        <th>Member</th>
                                                        <th>Scheme</th>
                                                        <th>Ins.</th>
                                                        <th>Due Date</th>
                                                        <th className="text-end">Principle</th>
                                                        <th className="text-end">Interest</th>
                                                        <th className="text-end">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {members.map((member) => (
                                                        <React.Fragment key={member.member_id}>
                                                            <tr className="bg-light">
                                                                <td>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={member.dues.every(
                                                                            (d) => selected[d.entry_id]
                                                                        )}
                                                                        onChange={() => toggleMember(member)}
                                                                    />
                                                                </td>
                                                                <td colSpan="6">
                                                                    <b>{member.member_name}</b> —{" "}
                                                                    {member.due_count} due(s)
                                                                </td>
                                                                <td className="text-end">
                                                                    <b>
                                                                        ₹
                                                                        {Number(
                                                                            member.due_amount
                                                                        ).toLocaleString("en-IN")}
                                                                    </b>
                                                                </td>
                                                            </tr>
                                                            {member.dues.map((due) => (
                                                                <tr key={due.entry_id}>
                                                                    <td>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={!!selected[due.entry_id]}
                                                                            onChange={() =>
                                                                                toggleDue(due.entry_id)
                                                                            }
                                                                        />
                                                                    </td>
                                                                    <td></td>
                                                                    <td>{due.scheme_name}</td>
                                                                    <td>{due.ins}</td>
                                                                    <td>
                                                                        {moment(due.due_date).format(
                                                                            "DD-MM-YYYY"
                                                                        )}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        {Number(due.principle).toFixed(2)}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        {Number(due.interest).toFixed(2)}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        {Number(due.total).toFixed(2)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div>
                                                Selected: <b>{selectedIds.length}</b> of{" "}
                                                {totalDueCount} due(s) — total{" "}
                                                <b>
                                                    ₹{selectedAmount.toLocaleString("en-IN")}
                                                </b>
                                            </div>
                                            <button
                                                className="btn btn-success"
                                                onClick={handlePost}
                                                disabled={posting || !selectedIds.length}
                                            >
                                                {posting ? "Posting..." : "Auto Posting"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
